import { FC, useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { Building, Floor, Town } from 'src/api/types';
import { FloorChipItem } from 'src/components/FloorChips/FloorChips';
import { FloorMapProvider } from 'src/components/FloorMap/FloorMapProvider';
import { Point } from 'src/components/FloorMap/types';
import { Page } from 'src/components/Page/Page';
import { useIdleTimer } from 'src/hooks/useIdleTimer';
import { useMapStyle } from 'src/hooks/useMapStyle';
import { FullscreenContainer } from 'src/providers/FullscreenProvider/FullscreenContainer/FullscreenContainer';
import { useFullscreenElement } from 'src/providers/FullscreenProvider/FullscreenProvider';
import {
  SearchBookingObjectProvider,
  useSearchBookingObject,
} from 'src/providers/SearchBookingObjectProvider';
import { SidesheetSlot } from 'src/providers/SidesheetSlot';
import { RoutePath } from 'src/routes/types';
import {
  getDeviceSize,
  getIsMobileWithTouchscreen,
} from 'src/store/agent/selectors';
import { getLastBookingsState } from 'src/store/lastBookings/selectors';
import { navigationActions } from 'src/store/navigation/slice';
import {
  getParkingLocation,
  getParkingState,
} from 'src/store/parking/selectors';
import { fetchParkingPlaces } from 'src/store/parking/services';
import {
  openParking,
  parkingActions,
  parkingPlacesPollingStarted,
  parkingPlacesPollingStopped,
} from 'src/store/parking/slice';
import { getSidesheetMode } from 'src/store/sidesheet/selectors';
import { setSidesheetSlots, sidesheetActions } from 'src/store/sidesheet/slice';
import { SidesheetSlotItem } from 'src/store/sidesheet/types';
import { useAppDispatch } from 'src/store/useAppDispatch';
import { DeviceSize } from 'src/types';

import { FloorChipsProvider } from '../../components/FloorChips/FloorChipsContext';

import { ParkingBookingPageActions } from './ParkingBookingPageActions/ParkingBookingPageActions';
import { ParkingBookingBottomSheet } from './ParkingBookingSidesheet/ParkingBookingBottomSheet/ParkingBookingBottomSheet';
import { ParkingFloorMap } from './ParkingFloorMap';
import { ParkingMapSettingsBottomSheet } from './ParkingMapSettingsBottomSheet/ParkingMapSettingsBottomSheet';
import classes from './ParkingPage.module.scss';

const modalSidesheetSizes = new Set([
  DeviceSize.XXS,
  DeviceSize.XS,
  DeviceSize.S,
]);

const filterFloorItems = (item: FloorChipItem): boolean =>
  !!item.value.isParking;

const PageLayout: FC = () => {
  const { fullscreen } = useFullscreenElement();
  const sidesheetMode = useSelector(getSidesheetMode);
  const [fixed, mapStyle] = useMapStyle();
  const isMobile = useSelector(getIsMobileWithTouchscreen);

  return (
    <Page
      actions={<ParkingBookingPageActions />}
      testId='parkingBooking'
      fullscreen={fullscreen}
      isSidesheetOpened={sidesheetMode === 'standard'}
      fixed={fixed}
      contentClassName={isMobile ? classes.mobile : undefined}
    >
      <div style={mapStyle}>
        <ParkingFloorMap />
      </div>
      {isMobile && (
        <>
          <ParkingMapSettingsBottomSheet />
          <ParkingBookingBottomSheet />
        </>
      )}
    </Page>
  );
};

const Inner: FC = () => {
  const size = useSelector(getDeviceSize);
  const { fullscreen } = useFullscreenElement();
  const { floor, loading } = useSelector(getParkingLocation);
  const { backgroundData, bookingObjects } = useSelector(getParkingState);
  const { parking } = useSelector(getLastBookingsState);

  const dispatch = useAppDispatch();

  const startPolling = useCallback(() => {
    dispatch(parkingPlacesPollingStarted(true));
  }, [dispatch]);

  const stopPolling = useCallback(() => {
    dispatch(parkingPlacesPollingStopped());
  }, [dispatch]);

  useEffect(() => {
    const standardSlotId = fullscreen ? 'pageLevel' : 'tabsLevel';
    const modalSlotId = fullscreen ? 'pageLevelModal' : 'tabsLevelModal';
    const standardSlot: SidesheetSlotItem = {
      id: standardSlotId,
      mode: 'standard',
    };
    const modalSlot: SidesheetSlotItem = {
      id: modalSlotId,
      mode: 'modal',
    };
    const isModalSlot = modalSidesheetSizes.has(size);

    dispatch(
      setSidesheetSlots([
        {
          sidesheetId: 'parkingBooking',
          slot: isModalSlot ? modalSlot : standardSlot,
        },
        {
          sidesheetId: 'parkingOffice',
          slot: modalSlot,
        },
      ]),
    );
    dispatch(
      navigationActions.setSegments([RoutePath.Main, RoutePath.Parking]),
    );
  }, [dispatch, fullscreen, size]);

  const init = useCallback(async () => {
    if (floor?.floorId) {
      await dispatch(parkingPlacesPollingStopped());
      await dispatch(fetchParkingPlaces(true));
      await dispatch(parkingPlacesPollingStarted(false));
    }
  }, [dispatch, floor?.floorId]);

  useEffect(() => {
    init();
  }, [init]);

  useIdleTimer({
    idleMs: 60000,
    onPause: stopPolling,
    onResume: startPolling,
  });

  const { searchedBookingObjectRef, setSearchedBookingObject } =
    useSearchBookingObject();

  const lastParkingId = useMemo(() => {
    if (parking && floor && parking.floor.floorId === floor.floorId) {
      return parking.placeId.toString();
    }
  }, [parking, floor]);

  const searchedParkingId = searchedBookingObjectRef.current?.id.toString();

  const offset = useMemo((): Point | undefined => {
    const offsetParkingId = searchedParkingId ?? lastParkingId;
    if (!offsetParkingId) return;

    return bookingObjects.get(offsetParkingId);
  }, [bookingObjects, lastParkingId, searchedParkingId]);

  useEffect(() => {
    if (searchedParkingId) {
      dispatch(openParking(searchedParkingId));
    }
  }, [dispatch, searchedParkingId, setSearchedBookingObject]);

  return (
    <FloorMapProvider
      floor={floor}
      backgroundData={backgroundData}
      floorLoading={loading}
      offset={offset}
    >
      <FullscreenContainer>
        <SidesheetSlot
          id='pageLevelModal'
          mode='modal'
          hasOverlay
          elevation='above'
        >
          <SidesheetSlot id='pageLevel'>
            <PageLayout />
          </SidesheetSlot>
        </SidesheetSlot>
      </FullscreenContainer>
    </FloorMapProvider>
  );
};

export const ParkingPage: FC = () => {
  const { building, floor } = useSelector(getParkingLocation);

  const dispatch = useAppDispatch();

  const setTown = useCallback(
    (value: Town | undefined) => {
      if (value) dispatch(parkingActions.setTown(value));
    },
    [dispatch],
  );

  const setBuilding = useCallback(
    (value: Building | undefined) => {
      if (value) dispatch(parkingActions.setBuilding(value));
    },
    [dispatch],
  );

  const setFloor = useCallback(
    (value: Floor | undefined) => {
      if (value) {
        dispatch(sidesheetActions.close());
        dispatch(parkingActions.setFloor(value));
      }
    },
    [dispatch],
  );

  const setTimeRange = useCallback(
    (from: Date | undefined, to: Date | undefined) => {
      dispatch(parkingActions.setTimeRange({ from, to }));
    },
    [dispatch],
  );

  return (
    <SearchBookingObjectProvider
      setTown={setTown}
      setBuilding={setBuilding}
      setFloor={setFloor}
      setTimeRange={setTimeRange}
    >
      <FloorChipsProvider
        floor={floor}
        setFloor={setFloor}
        buildingId={building?.buildingId}
        filterItems={filterFloorItems}
      >
        <Inner />
      </FloorChipsProvider>
    </SearchBookingObjectProvider>
  );
};

export default ParkingPage;
